import React, { useContext, useState, useEffect, FC, useRef } from 'react';
import { Grid } from '@mui/material';
import { withPrismicPreview } from 'gatsby-plugin-prismic-previews';
import { graphql, PageProps } from 'gatsby';
import debounce from 'lodash/debounce';

import {
  useCheckFavoriteWorkout,
  useCheckFavoriteProgram,
  useGridSize,
  useWindowSize,
  useRemoveFavoriteWorkout,
  useRemoveFavoriteProgram,
} from '@hooks';
import { AlbumCard, VideoCard } from '@lesmills-international/components';
import { Carousel, SlicePlaceholder } from '@components/atoms';
import Layout from '@components/molecules/layout';
import { withPrivateRoute } from '@components/molecules';
import { getWorkoutForVideoCard } from '@services';
import { addErrorInDatadogRum } from '../../utils/utilities';
import { getSlidesPerView } from '../../services/getCarouselSlides';
import { userContext } from '../../context/userContext';
import {
  MainTitle,
  SectionTitle,
  MyWorkoutsWrapper,
  SlicePlaceholderWrapper,
  SectionContainer,
  NoResultsWrapper,
  StyledHeartIcon,
  FavouriteContentContainer,
  FavoriteButton,
  StyledHeartOutlineIcon,
} from './styles';

import type { ExtendedUserInfo, WorkoutItem } from '../../services/getWorkoutForVideoCard';

interface MyWorkoutsPageData {
  meta_description: string;
  meta_title: string;
  no_programs_message: string;
  no_workouts_message: string;
  program_heading: string;
  workout_heading: string;
  page_title: {
    text: string;
  };
}

interface MyWorkoutsPageProps {
  data: {
    prismicFavoriteWorkouts: {
      data: MyWorkoutsPageData;
    };
  };
}

const FavouriteContentPlaceholder = ({ title }: { title: string }) => (
  <SectionContainer>
    <SectionTitle>{title}</SectionTitle>
    <SlicePlaceholderWrapper>
      <SlicePlaceholder height="200px" />
    </SlicePlaceholderWrapper>
  </SectionContainer>
);

const MyWorkouts: FC<PageProps<Queries.GetMyWorkoutsDataQuery, MyWorkoutsPageProps>> = ({
  data,
}) => {
  const [carouselSlidesPerView, setCarouselSlidesPerView] = useState(4);
  const [localFavoriteWorkouts, setLocalFavoriteWorkouts] = useState<WorkoutItem[]>([]); // local state for favorite workouts
  const [localItemsTotal, setLocalItemsTotal] = useState<number>(0); // local state for total number of favorite workouts
  const [localFavoritePrograms, setLocalFavoritePrograms] = useState<any[]>([]); // local state for favorite programs

  const { removeFavoriteWorkout, isProcessing: isRemovingFavoriteWorkout } =
    useRemoveFavoriteWorkout();
  const { removeFavoriteProgram, isProcessing: isRemovingFavoriteProgram } =
    useRemoveFavoriteProgram();
  const { favoritePrograms, loading: programsLoading } = useCheckFavoriteProgram();
  const {
    favoriteWorkouts,
    loading: workoutsLoading,
    fetchNextPage,
    nextToken,
    itemsTotal,
  } = useCheckFavoriteWorkout({});
  const userInfo = useContext(userContext);

  const { gridSize } = useGridSize();
  const windowInnerWidth = useWindowSize();

  const paginationLoadingRef = useRef(false);

  const { prismicFavoriteWorkouts } = data || {};
  const pageData = prismicFavoriteWorkouts?.data;

  // Calculate responsive slides per view
  useEffect(() => {
    const slidesPerView = getSlidesPerView(windowInnerWidth);
    setCarouselSlidesPerView(slidesPerView);
  }, [windowInnerWidth]);

  // Update local state when favoriteWorkouts changes
  useEffect(() => {
    if (favoriteWorkouts && itemsTotal) {
      setLocalFavoriteWorkouts(favoriteWorkouts);
      setLocalItemsTotal(itemsTotal);
    }
  }, [favoriteWorkouts, itemsTotal]);

  // Update local state when favoritePrograms changes
  useEffect(() => {
    if (favoritePrograms) {
      setLocalFavoritePrograms(favoritePrograms);
    }
  }, [favoritePrograms]);

  // Get more workouts when users scroll to the bottom of the page
  useEffect(() => {
    const handleScroll = async () => {
      if (
        window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 200 &&
        nextToken &&
        !workoutsLoading &&
        !paginationLoadingRef.current
      ) {
        paginationLoadingRef.current = true;
        await fetchNextPage();
        paginationLoadingRef.current = false;
      }
    };

    const debouncedScroll = debounce(handleScroll, 200);

    window.addEventListener('scroll', debouncedScroll);
    return () => window.removeEventListener('scroll', debouncedScroll);
  }, [nextToken, workoutsLoading, fetchNextPage]);

  const loading = programsLoading || (workoutsLoading && !paginationLoadingRef.current);

  const handleRemoveFavoriteWorkout = async (
    programName: string | undefined,
    workoutName: string | undefined
  ) => {
    if (!workoutName || !programName) return;

    try {
      // Optimistically remove from local state
      setLocalFavoriteWorkouts((prev) => prev.filter((workout) => workout.name !== workoutName));
      setLocalItemsTotal((prev) => Math.max(0, prev - 1));

      await removeFavoriteWorkout({ programName, workoutName });
    } catch (error) {
      // If the API call fails, revert the optimistic update
      setLocalFavoriteWorkouts((prev) => {
        const workout = favoriteWorkouts?.find((w) => w.name === workoutName);
        return workout ? [...prev, workout] : prev;
      });
      setLocalItemsTotal((prev) => Math.max(0, prev + 1));
      addErrorInDatadogRum(error);
    }
  };

  const handleRemoveFavoriteProgram = async (programName: string | undefined) => {
    if (!programName) return;

    try {
      // Optimistically remove from local state
      setLocalFavoritePrograms((prev) => prev.filter((program) => program.name !== programName));

      await removeFavoriteProgram(programName);
    } catch (error) {
      // If the API call fails, revert the optimistic update
      setLocalFavoritePrograms((prev) => {
        const program = favoritePrograms?.find((p) => p.name === programName);
        return program ? [...prev, program] : prev;
      });
      addErrorInDatadogRum(error);
    }
  };

  if (loading) {
    return (
      <Layout
        metaData={{
          title: pageData?.meta_title,
          description: pageData?.meta_description,
        }}
      >
        <MyWorkoutsWrapper>
          <MainTitle>{pageData?.page_title?.text}</MainTitle>

          {/* Programs Section - Loading Placeholder */}
          <FavouriteContentPlaceholder title={pageData?.program_heading || ''} />

          {/* Workouts Section - Loading Placeholder */}
          <FavouriteContentPlaceholder title={pageData?.workout_heading || ''} />
        </MyWorkoutsWrapper>
      </Layout>
    );
  }

  return (
    <Layout
      metaData={{
        title: pageData?.meta_title,
        description: pageData?.meta_description,
      }}
    >
      <MyWorkoutsWrapper>
        <MainTitle>{pageData?.page_title?.text}</MainTitle>

        {/* Programs Section */}
        <SectionContainer>
          <SectionTitle>
            {pageData?.program_heading} ({localFavoritePrograms?.length || 0})
          </SectionTitle>
          {localFavoritePrograms && localFavoritePrograms?.length > 0 ? (
            <Carousel
              items={
                localFavoritePrograms?.map((program, index) => (
                  <FavouriteContentContainer key={index}>
                    <AlbumCard
                      title={program?.name}
                      backgroundImg={program?.imageWide}
                      link={`/program/${program?.name?.replaceAll("'", '-').replaceAll(' ', '-').toLowerCase()}`}
                    />
                    <FavoriteButton
                      onClick={() => handleRemoveFavoriteProgram(program?.name)}
                      disabled={isRemovingFavoriteProgram}
                    >
                      <StyledHeartIcon />
                    </FavoriteButton>
                  </FavouriteContentContainer>
                )) || []
              }
              slidesPerView={carouselSlidesPerView}
              slidesPerGroup={1}
              spaceBetween={16}
              alwaysShowArrowButton
            />
          ) : (
            <NoResultsWrapper>
              {pageData?.no_programs_message_start}
              <StyledHeartOutlineIcon />
              {pageData?.no_programs_message_end}
            </NoResultsWrapper>
          )}
        </SectionContainer>

        {/* Workouts Section */}
        <SectionContainer>
          <SectionTitle>
            {pageData?.workout_heading} ({localItemsTotal || 0})
          </SectionTitle>
          {localFavoriteWorkouts && localFavoriteWorkouts?.length > 0 ? (
            <Grid container spacing={2} role="list" sx={{ '& .videoCard': { height: '100%' } }}>
              {localFavoriteWorkouts?.map((workout) => (
                <Grid item xs={gridSize} role="listitem" key={workout?.name}>
                  <FavouriteContentContainer>
                    <VideoCard
                      {...getWorkoutForVideoCard({
                        item: workout as WorkoutItem,
                        userInfo: userInfo as ExtendedUserInfo,
                      })}
                    />
                    <FavoriteButton
                      onClick={() =>
                        handleRemoveFavoriteWorkout(workout?.programName, workout?.name)
                      }
                      disabled={isRemovingFavoriteWorkout}
                    >
                      <StyledHeartIcon />
                    </FavoriteButton>
                  </FavouriteContentContainer>
                </Grid>
              ))}
            </Grid>
          ) : (
            <NoResultsWrapper>
              {pageData?.no_workouts_message_start}
              <StyledHeartOutlineIcon />
              {pageData?.no_workouts_message_end}
            </NoResultsWrapper>
          )}
        </SectionContainer>
      </MyWorkoutsWrapper>
    </Layout>
  );
};

export const query = graphql`
  query GetMyWorkoutsData($lang: String!) {
    prismicFavoriteWorkouts(lang: { eq: $lang }) {
      data {
        meta_description
        meta_title
        page_title {
          text
        }
        no_workouts_message_start
        no_workouts_message_end
        no_programs_message_start
        no_programs_message_end
        program_heading
        workout_heading
      }
    }
  }
`;

export default withPrismicPreview(withPrivateRoute(MyWorkouts));
